import React from "react";
import { useT } from "@transifex/react";
import { useTestCreationContext } from "../../../../context/TestCreationContext";
import { Exercises, ExerciseTypes } from "../../../../enums/exercises";
import { getExerciseDirection } from "../../../../helpers/TestCreation";
import { convertCreateTextToFreeText, getMinWordsForExerciseType } from "../../../../helpers/ExerciseTypesHelper";
import useExerciseDescription from "../../../../hooks/useExerciseDescription";
import AddVocabularyButton from "./detailsSubComponents/addVocabularyButton/AddVocabularyButton";
import DetailsConnectWords from "./details/detailsConnectWords/DetailsConnectWords";
import DetailsFreeText from "./details/detailsFreeText/DetailsFreeText";
import DetailsStandard from "./details/detailsStandard/DetailsStandard";
import DetailsGapSentence from "./details/detailsGapSentence/DetailsGapSentence";
import DetailsTextInput from "./details/detailsTextInput/DetailsTextInput";

import { InfoIconWrapper, NoWordsWrapper, StyledInfoIcon, Wrapper } from "./StyledComponents";

export interface ExerciseDetailsProps {
    hasWords?: boolean;
    isSelectExerciseVocabularyOpen: boolean;
    onClickAddVocabularyButton: () => void;
}

const ExerciseDetails: React.FC<ExerciseDetailsProps> = (props) => {
    const { currentExerciseModel, setCurrentExerciseModel, openWarningModal, setIsTestSynced, showScores } =
        useTestCreationContext();
    const { getDefaultDescription } = useExerciseDescription();

    const t = useT();

    const minWords = getMinWordsForExerciseType(currentExerciseModel.questionMode);

    const t_cantDeleteCardMin = t(
        "The word can't be deleted, as the minimum amount of cards per exercise is {minWords}.",
        {
            _tags: "CreateTest,CreateOrEditExercise",
            minWords: minWords,
        }
    );
    const t_noWordsYet = t("This exercise does not have any words yet.", {
        _tags: "CreateTest,CreateOrEditExercise",
    });

    function buildExerciseWithoutWord(wordId: string) {
        const exercise = { ...currentExerciseModel };

        exercise.wordsInProgress = (exercise.wordsInProgress ?? []).filter((w) => {
            if ("id" in w) {
                return w.id !== wordId;
            } else if ("ID" in w) {
                return w.ID !== wordId;
            }
            return false;
        });

        return exercise;
    }

    function removeWordFromExerciseWordsInProgress(wordId: string) {
        if (!currentExerciseModel.wordsInProgress?.length) return;

        const exercise = buildExerciseWithoutWord(wordId);

        if (currentExerciseModel.questionMode === ExerciseTypes.get(Exercises.STANDARD)) {
            exercise.direction = getExerciseDirection(exercise);
        }

        setCurrentExerciseModel(exercise);
    }

    function revertCreateTextToFreeText(wordId: string) {
        const exercise = convertCreateTextToFreeText(buildExerciseWithoutWord(wordId), getDefaultDescription);
        setCurrentExerciseModel(exercise);
    }

    function handleRemoveWord(wordId?: string) {
        if (!wordId) return;

        const wordsInProgress = currentExerciseModel.wordsInProgress;
        const isCreateText = currentExerciseModel.questionMode === ExerciseTypes.get(Exercises.CREATE_TEXT);

        // The CREATE_TEXT exercise is a "Textaufgabe" that auto-converted from FREE_TEXT when
        // its first word was added. Removing the last word reverts it back to FREE_TEXT
        // instead of blocking on the minimum-words rule.
        if (isCreateText && wordsInProgress && wordsInProgress.length <= minWords) {
            revertCreateTextToFreeText(wordId);
            setIsTestSynced(false);
            return;
        }

        if (wordsInProgress && wordsInProgress.length > minWords) {
            removeWordFromExerciseWordsInProgress(wordId);
            setIsTestSynced(false);
        } else {
            openWarningModal(t_cantDeleteCardMin);
        }
    }

    const renderDetails = () => {
        switch (currentExerciseModel.questionMode) {
            case ExerciseTypes.get(Exercises.CONNECT_WORDS):
                return (
                    <DetailsConnectWords
                        words={currentExerciseModel.wordsInProgress || []}
                        onRemoveWord={handleRemoveWord}
                        isSelectExerciseVocabularyOpen={props.isSelectExerciseVocabularyOpen}
                        onClickAddVocabularyButton={props.onClickAddVocabularyButton}
                    />
                );
            case ExerciseTypes.get(Exercises.FILL_GAP):
                return (
                    <DetailsGapSentence
                        hasWords={!!props.hasWords}
                        onRemoveWord={handleRemoveWord}
                        isSelectExerciseVocabularyOpen={props.isSelectExerciseVocabularyOpen}
                        onClickAddVocabularyButton={props.onClickAddVocabularyButton}
                        showScores={showScores}
                        currentExerciseModel={currentExerciseModel}
                    />
                );
            case ExerciseTypes.get(Exercises.WRITE_SENTENCE):
            case ExerciseTypes.get(Exercises.CREATE_TEXT):
                return (
                    <DetailsTextInput
                        hasWords={!!props.hasWords}
                        onRemoveWord={handleRemoveWord}
                        isSelectExerciseVocabularyOpen={props.isSelectExerciseVocabularyOpen}
                        onClickAddVocabularyButton={props.onClickAddVocabularyButton}
                        currentExerciseModel={currentExerciseModel}
                        showScores={showScores}
                    />
                );
            case ExerciseTypes.get(Exercises.FREE_TEXT):
                return (
                    <DetailsFreeText
                        isSelectExerciseVocabularyOpen={props.isSelectExerciseVocabularyOpen}
                        onClickAddVocabularyButton={props.onClickAddVocabularyButton}
                    />
                );
            default:
                return (
                    <DetailsStandard
                        onRemoveWord={handleRemoveWord}
                        isSelectExerciseVocabularyOpen={props.isSelectExerciseVocabularyOpen}
                        onClickAddVocabularyButton={props.onClickAddVocabularyButton}
                        currentExerciseModel={currentExerciseModel}
                        setCurrentExerciseModel={setCurrentExerciseModel}
                        setIsTestSynced={setIsTestSynced}
                        showScores={showScores}
                    />
                );
        }
    };

    const renderNoWordsYet = () => {
        return (
            <Wrapper>
                <NoWordsWrapper>
                    <InfoIconWrapper>
                        <StyledInfoIcon name={"info"} />
                    </InfoIconWrapper>
                    {t_noWordsYet}
                </NoWordsWrapper>
                {!props.isSelectExerciseVocabularyOpen && (
                    <AddVocabularyButton onClickAddVocabularyButton={props.onClickAddVocabularyButton} />
                )}
            </Wrapper>
        );
    };

    if (!props.hasWords && currentExerciseModel.questionMode !== ExerciseTypes.get(Exercises.FREE_TEXT)) {
        return renderNoWordsYet();
    } else {
        return renderDetails();
    }
};

export default ExerciseDetails;
